import {defineStore} from 'pinia'
import type {Maybe} from '../types'
import companyService, {type Address, type Attributes, type CompanyFeatureRequest} from '../services/CompanyService'
import CompanyPropertiesCookie from '../utils/CompanyPropertiesCookie'

interface State {
  id: Maybe<number>
  name: Maybe<string>
  dpaAccepted: Maybe<boolean>
  currency: Maybe<string>
  timeZone: Maybe<string>
  trustYouId: Maybe<string>
  autopilot: Maybe<boolean>
  federalState: Maybe<string>
  district: Maybe<string>
  initialized: boolean
  address: Maybe<Address>
  marketTrends: boolean
  maxRateRules: Maybe<number>
  isCompanyPropertiesCookieSet: boolean
  attributes: Maybe<Attributes>
  benchmarkingActive: boolean
  benchmarkingAdvancedActive: boolean
  featureRequests: Maybe<CompanyFeatureRequest[]>
}

const defaultState: State = {
  id: null,
  name: null,
  dpaAccepted: null,
  currency: null,
  timeZone: null,
  trustYouId: null,
  autopilot: null,
  initialized: false,
  address: null,
  marketTrends: false,
  maxRateRules: null,
  isCompanyPropertiesCookieSet: false,
  federalState: null,
  district: null,
  attributes: null,
  benchmarkingActive: false,
  benchmarkingAdvancedActive: false,
  featureRequests: null,
}

export const useCompanyStore = defineStore('company', {
  state: () => {
    return {...defaultState}
  },
  getters: {
    hasAutopilot: (state) => {
      return state.autopilot === true
    },
    isCompanySelected(state) {
      return state.id !== null
    },
  },
  actions: {
    async initialize() {
      if (!this.initialized) {
        return await Promise.all([this.update()])
      }
    },
    reset() {
      CompanyPropertiesCookie.remove()
      Object.assign(this, defaultState)
    },
    update() {
      const companyProperties = CompanyPropertiesCookie.get()
      if (companyProperties === undefined) {
        this.reset()
        this.isCompanyPropertiesCookieSet = false
        return
      } else {
        this.isCompanyPropertiesCookieSet = true
      }

      const companyId = companyProperties.id
      return Promise.all([
        companyService.fetchSingle(companyId),
        companyService.fetchFeatures(companyId),
        companyService.fetchFeatureRequests(companyId),
      ])
        .then(([company, features, featureRequests]) => {
          this.id = company.id
          this.name = company.name
          this.dpaAccepted = company.dataPrivacyAgreement?.accepted
          this.currency = company.currency
          this.timeZone = company.timeZone
          this.trustYouId = company.trustYouId
          this.federalState = company.federalState
          this.district = company.district
          this.address = company.address
          this.attributes = company.attributes
          this.autopilot = features.autopilot
          this.marketTrends = features.marketTrends
          this.maxRateRules = features.maxRateRules
          this.benchmarkingActive = features.benchmarking
          this.benchmarkingAdvancedActive = features.benchmarkingAdvanced === true
          this.featureRequests = featureRequests
        })
        .then(() => (this.initialized = true))
        .catch((error) => {
          console.error(error)
          this.reset()
        })
    },
  },
})
